import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Heart, Menu, Search, X, ShoppingBag } from "lucide-react";
import { useApp } from "../context/AppContext";
import { SunDoodle, WhatsAppIcon } from "./Illustrations";
import { whatsappLink } from "../data/site";
import { PERSONALITIES } from "../data/site";
import { COLLECTIONS } from "../data/collections";

export default function Nav() {
  const [solid, setSolid] = useState(false);
  const [open, setOpen] = useState(false);
  const { wishlist } = useApp();
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname, setOpen]);

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [setSolid]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${solid ? "glass" : "bg-transparent"}`}
        data-testid="site-nav"
      >
        <div className="mx-auto max-w-[1440px] px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
          {/* Mobile menu trigger */}
          <button
            onClick={() => setOpen(true)}
            className="md:hidden inline-flex items-center justify-center w-10 h-10 -ml-2"
            aria-label="Open menu"
            data-testid="nav-menu-open"
          >
            <Menu className="w-6 h-6" />
          </button>

          {/* Logo */}
          <Link to="/" className="flex items-center group" data-testid="nav-logo">
            <img
              src="/logo-trim.png"
              alt="SOL Kids"
              className="h-12 md:h-14 w-auto select-none"
              draggable="false"
            />
          </Link>

          {/* Desktop links */}
          <nav className="hidden md:flex items-center gap-8 text-[14px] font-medium">
            <NavLink to="/story" className="ink-link" data-testid="nav-story">Our Story</NavLink>
            <NavLink to="/collections" className="ink-link" data-testid="nav-collections">Collection</NavLink>
            <NavLink to="/personalities" className="ink-link" data-testid="nav-personalities">By Personality</NavLink>
            <NavLink to="/quiz" className="ink-link" data-testid="nav-quiz">Find Their Pair</NavLink>
            <NavLink to="/why-sol" className="ink-link" data-testid="nav-why">Why SOL</NavLink>
            <NavLink to="/faq" className="ink-link" data-testid="nav-faq">FAQ</NavLink>
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-2 md:gap-3">
            <a
              href={whatsappLink()}
              target="_blank" rel="noreferrer"
              className="hidden md:inline-flex items-center gap-2 rounded-full bg-[#2C2C2A] text-[#FAFAF7] px-4 py-2 text-[13px] font-medium hover:bg-[#1A1A18] transition-colors"
              data-testid="nav-whatsapp-cta"
            >
              <WhatsAppIcon size={14} color="#FAFAF7" />
              <span>WhatsApp</span>
            </a>
            <Link to="/wishlist" className="relative inline-flex items-center justify-center w-10 h-10" aria-label="Wishlist" data-testid="nav-wishlist">
              <Heart className="w-5 h-5" />
              {wishlist.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#EE8C72] text-white text-[10px] font-semibold flex items-center justify-center" data-testid="wishlist-count">
                  {wishlist.length}
                </span>
              )}
            </Link>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <MobileDrawer open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function MobileDrawer({ open, onClose }) {
  return (
    <div
      className={`fixed inset-0 z-[60] md:hidden ${open ? "pointer-events-auto" : "pointer-events-none"}`}
      aria-hidden={!open}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-[#2C2C2A]/40 transition-opacity duration-500 ${open ? "opacity-100" : "opacity-0"}`}
      />
      {/* Panel */}
      <aside
        className={`absolute top-0 left-0 h-full w-[88%] max-w-[420px] bg-[#FAFAF7] flex flex-col transition-transform duration-500 ${open ? "translate-x-0" : "-translate-x-full"}`}
        style={{ transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)" }}
        data-testid="mobile-drawer"
      >
        <div className="flex items-center justify-between px-5 h-16">
          <Link to="/" onClick={onClose} className="flex items-center">
            <img src="/logo-trim.png" alt="SOL Kids" className="h-12 w-auto" draggable="false" />
          </Link>
          <button onClick={onClose} aria-label="Close menu" data-testid="mobile-drawer-close" className="w-10 h-10 inline-flex items-center justify-center">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="px-7 pt-6 pb-10 overflow-y-auto flex-1">
          <p className="eyebrow mb-3">Shop</p>
          <ul className="space-y-2 mb-8">
            <li><Link onClick={onClose} to="/story" className="block font-serif text-3xl">Our Story</Link></li>
            <li><Link onClick={onClose} to="/collections" className="block font-serif text-3xl">Collection</Link></li>
            <li><Link onClick={onClose} to="/personalities" className="block font-serif text-3xl">By Personality</Link></li>
            <li><Link onClick={onClose} to="/quiz" className="block font-serif text-3xl" style={{ color: "#DD2E20" }}>Find Their Pair ✨</Link></li>
            <li><Link onClick={onClose} to="/why-sol" className="block font-serif text-3xl">Why SOL</Link></li>
            <li><Link onClick={onClose} to="/faq" className="block font-serif text-3xl">FAQ</Link></li>
          </ul>

          <p className="eyebrow mb-3">Discover</p>
          <ul className="space-y-2 mb-8">
            <li><Link onClick={onClose} to="/wishlist" className="block font-serif text-2xl">Wishlist</Link></li>
          </ul>

          <p className="eyebrow mb-3">Popular personalities</p>
          <div className="flex flex-wrap gap-2">
            {PERSONALITIES.slice(0, 5).map((p) => (
              <Link
                key={p.slug} to={`/personality/${p.slug}`} onClick={onClose}
                className="text-[13px] px-3 py-1.5 rounded-full bg-[#F3EADA] hover:bg-[#E8DCC4] transition-colors"
              >
                {p.emoji} {p.title}
              </Link>
            ))}
          </div>

          <div className="mt-10">
            <a
              href={whatsappLink()} target="_blank" rel="noreferrer"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white py-4 text-[15px] font-medium"
              data-testid="mobile-whatsapp-cta"
            >
              <WhatsAppIcon /> <span>Chat on WhatsApp</span>
            </a>
          </div>
        </div>
      </aside>
    </div>
  );
}
